import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Callout, Icon } from '@/nova/components';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import { formatCredits } from '@/nova/utils/credits';

import { useLandingDestinations } from '../../hooks/useLandingDestinations';
import { destinationHostPath, sameDestination } from '../../tracking-link';
import {
  quickCreateLandingPage,
  type QuickLandingAnswer,
  type QuickLandingRequest,
  type TrackingLink,
} from '../../tracking-links-api';
import { IngredientSection } from '../../../art-style/components/IngredientSection';
import { LumioMark } from '../LumioMark';

import { choiceForLink, choiceKey, choiceOf, sameChoice, type LandingChoice } from './link-choice';
import * as S from './LinkDestinationSheet.styles';

/** Where a link is sent when the owner confirms the casino or an existing landing page. */
export type DestinationChoice = { kind: 'casino' } | { kind: 'landing'; page: LandingChoice };

export interface LinkDestinationSheetProps {
  /** The brand (creatives tenant). */
  tenantId?: number;
  /** The casino destination (the promotion's page on the brand's website, else its homepage); absent when the brand
   *  has no website address yet (the choice is then disabled). */
  casino?: { url: string; promotionPage: boolean };
  /** The link as shown (null: the version has none yet, the footer then says "Create link"). Its destination is the
   *  sheet's starting choice. */
  link: TrackingLink | null;
  /** Send the link to the casino or a landing page: create it (first time) or move it (same `aff_id`). Resolves false
   *  when that did not go through (the sheet stays open and says so). */
  onDestination: (choice: DestinationChoice) => Promise<boolean>;
  /** The quick landing page's request (without `dry_run`): offered only when the asset has a subject. */
  quickRequest?: QuickLandingRequest;
  /** A quick landing page started: its page, the version's link (already pointing at it) and its run. */
  onQuickStarted?: (answer: QuickLandingAnswer) => void;
  /** Open the landing page composer for this asset ("Design a custom landing page"); absent: not offered. */
  onCustom?: () => void;
  /** Open Buy credits with what is missing (a quick landing page the balance does not cover). Absent: an inline
   *  notice. */
  onBuyCredits?: (needed?: number) => void;
  onClose: () => void;
}

/** The owner's choice in the sheet. `landing` without a page: the card is chosen but no page is picked yet. */
type SheetPick =
  | { kind: 'casino' }
  | { kind: 'landing'; page?: LandingChoice }
  | { kind: 'quick' }
  | { kind: 'custom' };

/** The quick landing page's price (its dry run). */
type QuickPrice =
  | { kind: 'loading' }
  | { kind: 'ready'; credits: number }
  | { kind: 'no-offer' }
  | { kind: 'error'; message: string };

/** The confirm in flight, or what stopped it. */
type Confirm =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'send-failed' }
  | { kind: 'quick-failed'; message: string }
  | { kind: 'credits'; needed?: number };

/** A quick landing page refusal: the service's `error` code (`no_offer`, `insufficient_credits`) or its message. */
type Refusal = { status: number; error?: string; needed?: number };
const isNoOffer = (res: Refusal): boolean => res.error === 'no_offer';
const isShort = (res: Refusal): boolean => res.status === 402 || res.error === 'insufficient_credits';

/** The sheet's starting choice: where the shown link lands, else the casino (when it has an address). */
const initialPick = (link: TrackingLink | null, hasCasino: boolean): SheetPick | null => {
  if (link) {
    const page = choiceForLink(link, []);
    return page ? { kind: 'landing', page } : { kind: 'casino' };
  }
  return hasCasino ? { kind: 'casino' } : null;
};

/**
 * "Where should this link send people?": the decision an asset's tracking link starts from (and returns to with
 * "Change"). Four choices, one at a time: straight to the casino (the promotion's page, else the homepage); one of the
 * brand's landing pages (the live list plus the Studio pages, the link's current page preselected); a quick landing
 * page Lumio builds and publishes for the asset's subject (its price read on open); or a custom one designed in the
 * landing page composer. The footer's button follows the choice. Moving a shown link keeps its `aff_id`.
 */
export const LinkDestinationSheet = ({
  tenantId,
  casino,
  link,
  onDestination,
  quickRequest,
  onQuickStarted,
  onCustom,
  onBuyCredits,
  onClose,
}: LinkDestinationSheetProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle, dismiss } = useSheetDismiss(onClose);
  const { destinations, loading: pagesLoading } = useLandingDestinations(tenantId);
  // Making a link or moving one, decided when the sheet opens (the link the first confirm makes must not flip the
  // footer while the sheet slides away).
  const [hasLink] = React.useState(() => !!link);
  const [pick, setPick] = React.useState<SheetPick | null>(() => initialPick(link, !!casino));
  // The page last picked in the list, kept while another card is chosen so coming back restores it.
  const [lastPage, setLastPage] = React.useState<LandingChoice | undefined>(() =>
    link ? choiceForLink(link, []) : undefined,
  );
  const [confirm, setConfirm] = React.useState<Confirm>({ kind: 'idle' });
  const busy = confirm.kind === 'busy';
  // A success closes the sheet once its busy flag has cleared: the Sheet ignores a close while busy, and the flag only
  // clears on the next render (closing in the same tick as clearing it was silently refused).
  const [closeWhenIdle, setCloseWhenIdle] = React.useState(false);
  // Syncs with the Sheet: closes it (its exit animation) once the confirm has settled.
  React.useEffect(() => {
    if (closeWhenIdle && !busy) dismiss();
  }, [closeWhenIdle, busy, dismiss]);

  // Where the shown link lands now, to preselect its row and tell an unchanged choice.
  const current = React.useMemo(() => (link ? choiceForLink(link, []) : undefined), [link]);

  // The rows: the brand's pages, plus the link's own page when the list does not show it (not live).
  const rows = React.useMemo(() => {
    const listed = destinations.map((d) => ({
      choice: choiceOf(d),
      name: d.name,
      where: destinationHostPath(d.url),
      imageUrl: d.imageUrl,
      state: 'live' as const,
    }));
    const unlisted = current && !destinations.some((d) => sameChoice(choiceOf(d), current)) ? current : undefined;
    if (!unlisted || pagesLoading) return listed;
    const pageState = link?.landing_page?.state;
    return [
      ...listed,
      {
        choice: unlisted,
        name: unlisted.name ?? '',
        where: unlisted.url ? destinationHostPath(unlisted.url) : '',
        imageUrl: undefined,
        state:
          pageState === 'draft' ? ('draft' as const) : pageState === 'live' ? ('live' as const) : ('offline' as const),
      },
    ];
  }, [destinations, current, pagesLoading, link]);

  // The quick landing page's price, read once when the sheet opens (nothing is created by a dry run).
  const [price, setPrice] = React.useState<QuickPrice>({ kind: 'loading' });
  const quickRef = React.useRef(quickRequest);
  quickRef.current = quickRequest;
  const offerQuick = !!quickRequest;
  React.useEffect(() => {
    // Syncs with the network: the quick landing page's price.
    const body = quickRef.current;
    if (!offerQuick || !body || !tenantId) return undefined;
    const ctrl = new AbortController();
    void quickCreateLandingPage(tenantId, { ...body, dry_run: true }, ctrl.signal).then((res) => {
      if (ctrl.signal.aborted) return;
      if (res.ok) setPrice({ kind: 'ready', credits: res.data.credits });
      else if (!res.aborted)
        setPrice(isNoOffer(res) ? { kind: 'no-offer' } : { kind: 'error', message: res.error ?? '' });
    });
    return () => ctrl.abort();
  }, [offerQuick, tenantId]);

  // A confirm answering after the sheet closed changes nothing here (one confirm runs at a time: `busy`).
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const choose = (next: SheetPick): void => {
    // A section opens even when its destination cannot be used yet (its body says why); confirming stays off.
    if (busy) return;
    setPick(next);
    if (next.kind === 'landing' && next.page) setLastPage(next.page);
    setConfirm({ kind: 'idle' });
  };

  const chooseLandingCard = (): void => {
    const remembered = lastPage && rows.some((r) => sameChoice(r.choice, lastPage)) ? lastPage : undefined;
    choose({ kind: 'landing', page: remembered ?? rows[0]?.choice });
  };

  /** The accordion's toggle: opening a section picks its destination, closing the open one picks nothing. */
  const toggle = (kind: SheetPick['kind']): void => {
    if (busy) return;
    if (pick?.kind === kind) {
      setPick(null);
      setConfirm({ kind: 'idle' });
      return;
    }
    if (kind === 'landing') chooseLandingCard();
    else if (kind === 'casino') choose({ kind: 'casino' });
    else if (kind === 'quick') choose({ kind: 'quick' });
    else choose({ kind: 'custom' });
  };

  const quickReady = price.kind === 'ready';
  // The chosen destination is where the shown link already lands: nothing to send.
  const unchanged =
    !!link &&
    ((pick?.kind === 'casino' &&
      link.destination_type === 'casino' &&
      sameDestination(link.destination_url, casino?.url)) ||
      (pick?.kind === 'landing' && !!pick.page && !!current && sameChoice(pick.page, current)));

  const canConfirm = ((): boolean => {
    if (!pick || busy) return false;
    switch (pick.kind) {
      case 'casino':
        return !!casino && !unchanged;
      case 'landing':
        return !!pick.page && !unchanged;
      case 'quick':
        return quickReady && !!tenantId && !!quickRequest;
      case 'custom':
        return !!onCustom;
      default:
        return false;
    }
  })();

  const send = async (choice: DestinationChoice): Promise<void> => {
    setConfirm({ kind: 'busy' });
    const ok = await onDestination(choice);
    if (!mountedRef.current) return;
    if (ok) {
      setConfirm({ kind: 'idle' });
      setCloseWhenIdle(true);
      return;
    }
    setConfirm({ kind: 'send-failed' });
  };

  const startQuick = async (): Promise<void> => {
    if (!tenantId || !quickRequest) return;
    setConfirm({ kind: 'busy' });
    const res = await quickCreateLandingPage(tenantId, quickRequest);
    if (!mountedRef.current) return;
    if (res.ok) {
      setConfirm({ kind: 'idle' });
      onQuickStarted?.(res.data);
      setCloseWhenIdle(true);
      return;
    }
    if (res.aborted) {
      setConfirm({ kind: 'idle' });
      return;
    }
    if (isNoOffer(res)) {
      setPrice({ kind: 'no-offer' });
      setConfirm({ kind: 'idle' });
      return;
    }
    if (isShort(res)) {
      // Buy credits opens over this sheet; once topped up, the owner builds again from here.
      setConfirm({ kind: 'credits', needed: res.needed });
      onBuyCredits?.(res.needed);
      return;
    }
    setConfirm({ kind: 'quick-failed', message: res.error ?? '' });
  };

  const onPrimary = (): void => {
    if (!canConfirm || !pick) return;
    switch (pick.kind) {
      case 'casino':
        void send({ kind: 'casino' });
        return;
      case 'landing':
        if (pick.page) void send({ kind: 'landing', page: pick.page });
        return;
      case 'quick':
        void startQuick();
        return;
      case 'custom':
        onCustom?.();
        return;
      default:
    }
  };

  const primaryLabel = ((): string => {
    if (pick?.kind === 'quick') {
      if (busy) return t('Starting…');
      return price.kind === 'ready'
        ? t('Build it · {{n}} credits', { n: formatCredits(price.credits) })
        : t('Build it');
    }
    if (pick?.kind === 'custom') return t('Open the composer');
    if (busy) return hasLink ? t('Saving…') : t('Creating link…');
    return hasLink ? t('Send link here') : t('Create link');
  })();

  const notice = ((): React.ReactNode => {
    switch (confirm.kind) {
      case 'send-failed':
        return (
          <Callout
            tone="danger"
            text={
              hasLink
                ? t('Could not change where the link goes. Please try again.')
                : t('Could not create the tracking link. Please try again.')
            }
          />
        );
      case 'quick-failed':
        return (
          <Callout
            tone="danger"
            title={t('Could not start the landing page')}
            text={confirm.message || t('Please try again.')}
          />
        );
      case 'credits':
        // Buy credits is open over the sheet; without it, say what is missing here.
        return onBuyCredits ? null : (
          <Callout
            tone="warning"
            title={t('Not enough credits')}
            text={
              confirm.needed
                ? t('This landing page needs {{n}} more credits.', { n: confirm.needed })
                : t('Top up your credits, then try again.')
            }
          />
        );
      default:
        return null;
    }
  })();

  const subjectName = quickRequest
    ? quickRequest.subject.name.trim() || quickRequest.subject.headline?.trim() || ''
    : '';
  const picked = (kind: SheetPick['kind']): boolean => pick?.kind === kind;
  const landingPage = pick?.kind === 'landing' ? pick.page : undefined;

  return (
    <Sheet
      title="Where should this link send people?"
      onClose={onClose}
      closeHandle={closeHandle}
      busy={busy}
      animateResize
      footer={
        <S.FooterStack>
          {notice}
          <S.FooterRow>
            <Button variant="text" label={t('Cancel')} onClick={dismiss} disabled={busy} />
            <Button variant="primary" label={primaryLabel} onClick={onPrimary} disabled={!canConfirm} />
          </S.FooterRow>
        </S.FooterStack>
      }
    >
      {/* Literal copy per branch: the extractor harvests only a string-literal `text`. */}
      {hasLink ? (
        <S.Intro text="The link itself stays the same, so ads already out keep working. Only where it sends people changes." />
      ) : (
        <S.Intro text="Pick where people land when they open this ad's tracking link." />
      )}
      {/* One section per destination, like the composers' Ingredients: the open one is the pick. Every section takes
          the height it needs, so the sheet's own scroll moves through them. */}
      <S.Sections>
        {/* a. Straight to the website (the casino site). */}
        <IngredientSection
          id="dest-casino"
          title={t('Straight to the website')}
          icon="home"
          summary={casino ? destinationHostPath(casino.url) : t('No website address yet')}
          open={picked('casino')}
          onToggle={() => toggle('casino')}
        >
          <S.SectionBody>
            {casino ? (
              <>
                {casino.promotionPage ? (
                  <S.ChoiceText text="The promotion's page on your website" />
                ) : (
                  <S.ChoiceText text="Your website's homepage" />
                )}
                <S.Where raw text={destinationHostPath(casino.url)} />
              </>
            ) : (
              <S.ChoiceReason text="This brand has no website address yet." />
            )}
          </S.SectionBody>
        </IngredientSection>

        {/* b. An existing landing page: every page listed at its full height. */}
        <IngredientSection
          id="dest-landing"
          title={t('An existing landing page')}
          icon="document"
          summary={
            pagesLoading
              ? undefined
              : rows.length === 0
                ? t('None yet')
                : rows.length === 1
                  ? t('1 landing page')
                  : t('{{n}} landing pages', { n: rows.length })
          }
          open={picked('landing')}
          onToggle={() => toggle('landing')}
        >
          <S.PageList role="radiogroup" aria-label={t('Landing pages')}>
            {pagesLoading ? (
              <>
                <S.PageRowSkeleton aria-hidden="true" />
                <S.PageRowSkeleton aria-hidden="true" />
              </>
            ) : rows.length === 0 ? (
              <S.Empty text="This brand has no landing page yet." />
            ) : (
              rows.map((row) => {
                const on = !!landingPage && sameChoice(landingPage, row.choice);
                // Only a live page has something to open (a draft has no address, an unpublished one sends visitors on).
                const openUrl = row.state !== 'draft' && row.state !== 'offline' ? row.choice.url.trim() : '';
                const pageLabel = row.name || t('Untitled landing page');
                return (
                  <S.PageItem key={choiceKey(row.choice)}>
                    <S.PageRow
                      type="button"
                      role="radio"
                      aria-checked={on}
                      $selected={on}
                      onClick={() => choose({ kind: 'landing', page: row.choice })}
                    >
                      <S.Thumb aria-hidden="true">
                        {row.imageUrl ? (
                          <S.ThumbImg src={row.imageUrl} alt="" loading="lazy" draggable={false} />
                        ) : (
                          <Icon name="globe" size={16} />
                        )}
                      </S.Thumb>
                      <S.PageCopy>
                        {row.name ? <S.PageName raw text={row.name} /> : <S.PageName text="Untitled landing page" />}
                        {row.state === 'draft' ? (
                          <S.ChoiceText text="Not published yet" />
                        ) : row.state === 'offline' ? (
                          row.where ? (
                            <S.ChoiceText text="Not live · {{where}}" placeholders={{ where: row.where }} />
                          ) : (
                            <S.ChoiceText text="Not live" />
                          )
                        ) : (
                          <S.Where raw text={row.where} />
                        )}
                      </S.PageCopy>
                      {openUrl ? <S.LinkSlot aria-hidden="true" /> : null}
                      <S.Radio $on={on} aria-hidden="true" />
                    </S.PageRow>
                    {openUrl ? (
                      <S.OpenLink
                        href={openUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t('Open {{name}} in a new tab', { name: pageLabel })}
                        title={t('Open {{name}} in a new tab', { name: pageLabel })}
                      >
                        <Icon name="external-link" size={16} />
                      </S.OpenLink>
                    ) : null}
                  </S.PageItem>
                );
              })
            )}
          </S.PageList>
        </IngredientSection>

        {/* c. Quick landing page: Lumio builds it (the Lumio mark, never a sparkle). */}
        {quickRequest ? (
          <IngredientSection
            id="dest-quick"
            title={t('Quick landing page')}
            icon="document"
            iconNode={<LumioMark size={16} />}
            summary={
              price.kind === 'ready'
                ? t('{{n}} credits', { n: formatCredits(price.credits) })
                : price.kind === 'no-offer'
                  ? t('Needs a marketing offer')
                  : undefined
            }
            open={picked('quick')}
            onToggle={() => toggle('quick')}
          >
            <S.SectionBody>
              {subjectName ? (
                <S.ChoiceText
                  text="Lumio builds and publishes a landing page for {{name}}"
                  placeholders={{ name: subjectName }}
                />
              ) : (
                <S.ChoiceText text="Lumio builds and publishes a landing page for this ad" />
              )}
              {price.kind === 'loading' ? <S.PriceSkeleton aria-hidden="true" /> : null}
              {price.kind === 'ready' ? (
                <S.Price text="{{n}} credits" placeholders={{ n: formatCredits(price.credits) }} />
              ) : null}
              {price.kind === 'no-offer' ? (
                <S.ChoiceReason text="This brand needs a marketing offer first: a quick landing page promotes it." />
              ) : null}
              {price.kind === 'error' ? (
                price.message ? (
                  <S.ChoiceReason raw text={price.message} />
                ) : (
                  <S.ChoiceReason text="Lumio cannot build a landing page right now. Please try again later." />
                )
              ) : null}
            </S.SectionBody>
          </IngredientSection>
        ) : null}

        {/* d. Design a custom landing page. */}
        {onCustom ? (
          <IngredientSection
            id="dest-custom"
            title={t('Design a custom landing page')}
            icon="edit"
            summary={t('Opens the composer')}
            open={picked('custom')}
            onToggle={() => toggle('custom')}
          >
            <S.SectionBody>
              <S.ChoiceText text="Open the landing page composer for this ad. The link sends people there once you publish the page." />
            </S.SectionBody>
          </IngredientSection>
        ) : null}
      </S.Sections>
    </Sheet>
  );
};

export default LinkDestinationSheet;
